import React from "react";
import moment, { Moment } from "moment";
import { TimePicker } from "@mui/x-date-pickers";
import { Box, Typography } from "@mui/material";
import { t } from "../../components/translate";
import DebouncedTextField from "../../components/DebouncedTextField";
import useAssessmentLabel from "../../hooks/useAssessmentLabel";
import { roundMinutes } from "../Calendar/Booking/BookingCreateEdit";
import CalendarDropdown from "../../components/CalendarDropdown/CalendarDropdown";
import { Followup } from "../../interfaces/AssessmentInterfaces";

// @ts-expect-error - css
import styles from "./Assessment.module.css";

interface FollowUpInterface {
  assessmentFollowUp: Followup;
  onChange: (updatedValues: Partial<Followup>) => void;
}

const FollowUp: React.FC<FollowUpInterface> = (props) => {
  const { assessmentFollowUp, onChange } = props;
  const assessmentLabels = useAssessmentLabel();
  return (
    <Box sx={{ display: "flex", alignItems: "flex-start", height: "auto" }}>
      <Box className={styles.assessmentLabel} sx={{ pt: "4px" }}>
        <Typography component="span" sx={{ fontWeight: "600" }}>
          {assessmentLabels.followup}
        </Typography>
      </Box>
      <Box className={styles.assessmentField}>
        <Box sx={{ display: "flex", gap: 1, flexDirection: "column" }}>
          <Box sx={{ display: "flex", gap: 1, flexWrap: "wrap" }}>
            <CalendarDropdown
              data-testmation="followUpNumberTextField"
              TextFieldProps={{ variant: "outlined" }}
              label="Follow up date"
              date={new Date(assessmentFollowUp.date || new Date())}
              minValidDate={new Date()}
              onChange={(val) => {
                const newDate = moment(val);
                const startDate = newDate
                  .set("hour", newDate.hour())
                  .set("minute", roundMinutes(newDate.minute(), 5) || 0)
                  .toDate();
                const endDate = moment(startDate).add(15, "minutes").toDate();

                onChange({
                  date: newDate.toISOString(),
                  startDate,
                  endDate
                });
              }}
            />
            <Box sx={{ display: "flex", gap: 1, alignItems: "center", flexWrap: "wrap" }}>
              <TimePicker
                skipDisabled
                timeSteps={{ minutes: 10 }}
                label="Start Time"
                value={moment(assessmentFollowUp.startDate || new Date())}
                onChange={(newTime: Moment | null) => {
                  if (!newTime) return;
                  const baseDate = moment(assessmentFollowUp.date);
                  const startDate = baseDate
                    .set("hour", newTime.hour())
                    .set("minute", roundMinutes(newTime.minute(), 5) || 0)
                    .toDate();
                  const endDate = moment(startDate).add(15, "minutes").toDate();
                  onChange({ startDate, endDate });
                }}
                slotProps={{
                  textField: {
                    variant: "outlined",
                    size: "small",
                    sx: {
                      width: "120px",
                      "& .MuiInputBase-root": { fontSize: "12px" }
                    }
                  }
                }}
              />
              <TimePicker
                skipDisabled
                timeSteps={{ minutes: 10 }}
                label="End Time"
                value={moment(assessmentFollowUp.endDate || new Date())}
                onChange={(newTime: Moment | null) => {
                  if (!newTime) return;
                  const baseDate = moment(assessmentFollowUp.date);
                  const newEnd = baseDate
                    .set("hour", newTime.hour())
                    .set("minute", roundMinutes(newTime.minute(), 5) || 0)
                    .toDate();
                  if (moment(newEnd).isBefore(assessmentFollowUp.startDate)) {
                    onChange({
                      endDate: moment(assessmentFollowUp.startDate).add(15, "minutes").toDate()
                    });
                    return;
                  }
                  onChange({ endDate: newEnd });
                }}
                slotProps={{
                  textField: {
                    variant: "outlined",
                    size: "small",
                    sx: {
                      width: "120px",
                      "& .MuiInputBase-root": { fontSize: "12px" }
                    }
                  }
                }}
              />
            </Box>
          </Box>
          <DebouncedTextField
            data-testmation="followUpRemarksTextField"
            variant="outlined"
            margin="dense"
            fullWidth
            placeholder={t("assessment.followUpRemarks")}
            slotProps={{
              inputLabel: { shrink: true }
            }}
            value={assessmentFollowUp.note}
            onChange={(e) => onChange({ note: e.target.value })}
            debounceAt={400}
          />
        </Box>
      </Box>
    </Box>
  );
};

export default FollowUp;
